import { ModalLogo, Plus } from "@assets"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as React from "react"

import { cn } from "@/lib/utils"

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  const [viewport, setViewport] = React.useState<{
    height: number
    offsetTop: number
  } | null>(null)

  React.useEffect(() => {
    const updateViewport = () => {
      if (window.visualViewport) {
        setViewport({
          height: window.visualViewport.height,
          offsetTop: window.visualViewport.offsetTop,
        })
      }
    }

    updateViewport()

    window.visualViewport?.addEventListener("resize", updateViewport)
    window.visualViewport?.addEventListener("scroll", updateViewport)

    return () => {
      window.visualViewport?.removeEventListener("resize", updateViewport)
      window.visualViewport?.removeEventListener("scroll", updateViewport)
    }
  }, [])

  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "bg-modal/95 fixed inset-0 z-50",
        className,
      )}
      {...props}
    >
      {/* Scroll container sized to visual viewport */}
      <div
        className="overflow-y-auto overscroll-contain"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          top: viewport ? viewport.offsetTop : 0,
          height: viewport ? viewport.height : "100dvh",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </div>
    </DialogPrimitive.Overlay>
  )
}

function DialogContent({
  className,
  children,
  showWelcomeLogo = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showWelcomeLogo?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay>
        {/* Centering wrapper with spacers for proper scroll behavior */}
        <div className="flex min-h-full flex-col items-center px-4 pt-[max(2rem,env(safe-area-inset-top))] pb-[max(2rem,env(safe-area-inset-bottom))]">
          <div className="min-h-4 flex-1" />
          <DialogPrimitive.Content
            data-slot="dialog-content"
            className={cn(
              "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "relative shrink-0 rounded-[30px] shadow-[0_0_12px_rgba(2,59,200,0.2)] duration-200",
              showWelcomeLogo
                ? "bg-card mt-[52px] flex w-[337px] max-w-[90vw] flex-col px-[30px] pt-[90px] pb-[30px] text-center"
                : "bg-card grid w-[340px] max-w-[90vw] gap-4 px-6 py-6 sm:w-[360px] md:w-[400px]",
              className,
            )}
            {...props}
          >
            {showWelcomeLogo && (
              <div
                className="absolute -top-[52px] left-1/2 flex w-[167px] -translate-x-1/2 justify-center"
                style={{ zIndex: 100 }}
              >
                <div className="relative h-[167px] w-[167px]">
                  <div className="dark:from-card absolute inset-x-0 top-0 mx-auto h-[129px] w-[128px] rounded-full bg-gradient-to-b from-white to-[#c7d2fe] shadow-[0_0_12px_rgba(85,123,244,0.25)] dark:to-[#333333]" />
                  <img
                    src={ModalLogo}
                    alt="Logo"
                    className="relative z-10 h-[297px] w-[167px] object-contain"
                    style={{ transform: "translateY(-100px)" }}
                  />
                </div>
              </div>
            )}
            {children}
            {/* Botón X siempre presente */}
            <DialogPrimitive.Close className="ring-offset-background data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
              <Plus
                className="text-primary size-5"
                stroke="#557BF4"
                strokeWidth={0.1}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: "rotate(45deg)", opacity: 1 }}
              />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
          <div className="min-h-4 flex-1" />
        </div>
      </DialogOverlay>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-foreground flex h-[25px] items-center justify-center gap-2 text-[18px] leading-[27px] font-semibold tracking-[0px]",
        className,
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
